export interface TarotCard {
    id: number;
    name: string;
    arcana: 'major' | 'minor';
    suit?: 'wands' | 'cups' | 'swords' | 'pentacles';
    number?: number;
    uprightMeaning: string;
    keywords: string[];
    reflectionPrompt: string;
    imageUrl: string; // Using internal assets or placeholders
}

export const tarotDeck: TarotCard[] = [
    // --- MAJOR ARCANA (22 Cards) ---
    {
        id: 0,
        name: "The Fool",
        arcana: "major",
        number: 0,
        uprightMeaning: "The Fool represents new beginnings, spontaneity, and a free spirit. It encourages you to take a leap of faith and trust in the universe. This card signals the start of a new journey where anything is possible.",
        keywords: ["New Beginnings", "Innocence", "Spontaneity", "Free Spirit"],
        reflectionPrompt: "Where is fear holding you back from a fresh start today?",
        imageUrl: "/assets/tarot/0_the_fool.jpg"
    },
    {
        id: 1,
        name: "The Magician",
        arcana: "major",
        number: 1,
        uprightMeaning: "The Magician represents manifestation, resourcefulness, and power. You have all the tools you need to create the life you desire. Use your skills and willpower to turn your dreams into reality.",
        keywords: ["Manifestation", "Resourcefulness", "Power", "Action"],
        reflectionPrompt: "What resources do you already possess that can help you achieve your goal?",
        imageUrl: "/assets/tarot/1_the_magician.jpg"
    },
    {
        id: 2,
        name: "The High Priestess",
        arcana: "major",
        number: 2,
        uprightMeaning: "The High Priestess signifies intuition, mystery, and inner knowledge. Trust your gut feelings and listen to the wisdom within. It is a time for stillness and connecting with your subconscious.",
        keywords: ["Intuition", "Mystery", "Subconscious", "Inner Voice"],
        reflectionPrompt: "What is your inner voice trying to tell you that you've been ignoring?",
        imageUrl: "/assets/tarot/2_high_priestess.jpg"
    },
    {
        id: 3,
        name: "The Empress",
        arcana: "major",
        number: 3,
        uprightMeaning: "The Empress embodies fertility, femininity, and abundance. She encourages you to connect with nature and nurture your creativity. Expect growth and prosperity in your endeavors.",
        keywords: ["Fertility", "Abundance", "Nature", "Nurturing"],
        reflectionPrompt: "How can you nurture yourself or a creative project today?",
        imageUrl: "/assets/tarot/3_the_empress.jpg"
    },
    {
        id: 4,
        name: "The Emperor",
        arcana: "major",
        number: 4,
        uprightMeaning: "The Emperor represents authority, structure, and control. He brings order to chaos and builds a solid foundation. It is a time to be disciplined and take charge of your life.",
        keywords: ["Authority", "Structure", "Control", "Fatherhood"],
        reflectionPrompt: "Where do you need more structure or discipline in your life right now?",
        imageUrl: "/assets/tarot/4_the_emperor.jpg"
    },
    {
        id: 5,
        name: "The Hierophant",
        arcana: "major",
        number: 5,
        uprightMeaning: "The Hierophant signifies spiritual wisdom, tradition, and conformity. It may suggest seeking guidance from a mentor or following established systems. Values and beliefs are highlighted.",
        keywords: ["Tradition", "Conformity", "Spiritual Wisdom", "Guidance"],
        reflectionPrompt: "What traditions or beliefs are currently guiding your decisions?",
        imageUrl: "/assets/tarot/5_the_hierophant.jpg"
    },
    {
        id: 6,
        name: "The Lovers",
        arcana: "major",
        number: 6,
        uprightMeaning: "The Lovers represent love, harmony, and partnerships. It can also signify a major choice or alignment of values. Relationships are in focus, whether romantic or otherwise.",
        keywords: ["Love", "Harmony", "Relationships", "Choices"],
        reflectionPrompt: "Are your choices aligned with your true values?",
        imageUrl: "/assets/tarot/6_the_lovers.jpg"
    },
    {
        id: 7,
        name: "The Chariot",
        arcana: "major",
        number: 7,
        uprightMeaning: "The Chariot indicates victory, determination, and willpower. You are moving forward with purpose and overcoming obstacles. Stay focused and disciplined to achieve success.",
        keywords: ["Victory", "Willpower", "Determination", "Control"],
        reflectionPrompt: "What goal are you determined to reach, no matter the obstacles?",
        imageUrl: "/assets/tarot/7_the_chariot.jpg"
    },
    {
        id: 8,
        name: "Strength",
        arcana: "major",
        number: 8,
        uprightMeaning: "Strength represents courage, persuasion, and inner fortitude. It is not about brute force but gentle control and compassion. You have the strength to endure and overcome.",
        keywords: ["Courage", "Compassion", "Inner Strength", "Patience"],
        reflectionPrompt: "How can you approach a difficult situation with compassion rather than force?",
        imageUrl: "/assets/tarot/8_strength.jpg"
    },
    {
        id: 9,
        name: "The Hermit",
        arcana: "major",
        number: 9,
        uprightMeaning: "The Hermit suggests soul-searching, introspection, and being alone. It is a time to withdraw from the noise of the world and seek your own inner light and guidance.",
        keywords: ["Introspection", "Solitude", "Guidance", "Inner Light"],
        reflectionPrompt: "What truth might you find in silence today?",
        imageUrl: "/assets/tarot/9_the_hermit.jpg"
    },
    {
        id: 10,
        name: "Wheel of Fortune",
        arcana: "major",
        number: 10,
        uprightMeaning: "The Wheel of Fortune signifies luck, karma, and life cycles. Change is inevitable, and the wheel is turning in your favor. Embrace the ups and downs as part of your destiny.",
        keywords: ["Luck", "Karma", "Cycles", "Destiny"],
        reflectionPrompt: "How can you flow with changes rather than resisting them?",
        imageUrl: "/assets/tarot/10_wheel_of_fortune.jpg"
    },
    {
        id: 11,
        name: "Justice",
        arcana: "major",
        number: 11,
        uprightMeaning: "Justice represents fairness, truth, and law. Actions have consequences, and balance will be restored. Seek the truth and act with integrity.",
        keywords: ["Fairness", "Truth", "Law", "Cause and Effect"],
        reflectionPrompt: "In what area of your life do you need to be more honest with yourself?",
        imageUrl: "/assets/tarot/11_justice.jpg"
    },
    {
        id: 12,
        name: "The Hanged Man",
        arcana: "major",
        number: 12,
        uprightMeaning: "The Hanged Man suggests pause, surrender, and letting go. You may need to look at things from a different perspective or sacrifice something to gain wisdom.",
        keywords: ["Surrender", "New Perspective", "Pause", "Sacrifice"],
        reflectionPrompt: "What control do you need to surrender to move forward?",
        imageUrl: "/assets/tarot/12_hanged_man.jpg"
    },
    {
        id: 13,
        name: "Death",
        arcana: "major",
        number: 13,
        uprightMeaning: "Death signifies endings, change, and transformation. It is not literal death but the end of a cycle to make way for the new. Embrace the transition.",
        keywords: ["Endings", "Transformation", "Transition", "Change"],
        reflectionPrompt: "What do you need to let go of so something new can begin?",
        imageUrl: "/assets/tarot/13_death.jpg"
    },
    {
        id: 14,
        name: "Temperance",
        arcana: "major",
        number: 14,
        uprightMeaning: "Temperance represents balance, moderation, and patience. It calls for blending opposites and finding the middle ground. Healing and harmony are available.",
        keywords: ["Balance", "Moderation", "Patience", "Harmony"],
        reflectionPrompt: "Where in your life do you need to find more balance?",
        imageUrl: "/assets/tarot/14_temperance.jpg"
    },
    {
        id: 15,
        name: "The Devil",
        arcana: "major",
        number: 15,
        uprightMeaning: "The Devil signifies shadow self, attachment, and addiction. You may feel trapped by your own desires or material bonds. Awareness is the first step to freedom.",
        keywords: ["Addiction", "Materialism", "Playfulness", "Shadow Self"],
        reflectionPrompt: "What habits or beliefs are currently chaining you down?",
        imageUrl: "/assets/tarot/15_the_devil.jpg"
    },
    {
        id: 16,
        name: "The Tower",
        arcana: "major",
        number: 16,
        uprightMeaning: "The Tower represents sudden change, upheaval, and chaos. Though difficult, it breaks down false structures to reveal the truth. Rebuild on a stronger foundation.",
        keywords: ["Sudden Change", "Upheaval", "Chaos", "Revelation"],
        reflectionPrompt: "What shaky foundation in your life is ready to be rebuilt?",
        imageUrl: "/assets/tarot/16_the_tower.jpg"
    },
    {
        id: 17,
        name: "The Star",
        arcana: "major",
        number: 17,
        uprightMeaning: "The Star signifies hope, faith, and purpose. After the storm comes the calm. You are being blessed by the universe and inspired to follow your dreams.",
        keywords: ["Hope", "Faith", "Purpose", "Renewal"],
        reflectionPrompt: "What hope inspires you to keep effective action towards your dreams?",
        imageUrl: "/assets/tarot/17_the_star.jpg"
    },
    {
        id: 18,
        name: "The Moon",
        arcana: "major",
        number: 18,
        uprightMeaning: "The Moon represents illusion, fear, and anxiety. Not everything is as it seems. Trust your intuition to navigate through the uncertainty and subconscious fears.",
        keywords: ["Illusion", "Fear", "Anxiety", "Intuition"],
        reflectionPrompt: "What fears are clouding your judgment right now?",
        imageUrl: "/assets/tarot/18_the_moon.jpg"
    },
    {
        id: 19,
        name: "The Sun",
        arcana: "major",
        number: 19,
        uprightMeaning: "The Sun signifies positivity, fun, warmth, and success. It is a card of joy and vitality. Everything is falling into place, and you are radiating confidence.",
        keywords: ["Positivity", "Fun", "Warmth", "Success"],
        reflectionPrompt: "How can you share your joy and light with others today?",
        imageUrl: "/assets/tarot/19_the_sun.jpg"
    },
    {
        id: 20,
        name: "Judgement",
        arcana: "major",
        number: 20,
        uprightMeaning: "Judgement represents rebirth, inner calling, and absolution. You are at a crossroads and called to rise to a higher level of consciousness. Review your past and make a fresh start.",
        keywords: ["Rebirth", "Inner Calling", "Absolution", "Awakening"],
        reflectionPrompt: "What past judgment are you ready to release to move forward?",
        imageUrl: "/assets/tarot/20_judgement.jpg"
    },
    {
        id: 21,
        name: "The World",
        arcana: "major",
        number: 21,
        uprightMeaning: "The World signifies completion, integration, and accomplishment. You have reached a major milestone or the end of a cycle. Celebrate your success and the wholeness you feel.",
        keywords: ["Completion", "Integration", "Accomplishment", "Travel"],
        reflectionPrompt: "What cycle are you closing out, and what have you learned?",
        imageUrl: "/assets/tarot/21_the_world.jpg"
    },

    // --- MINOR ARCANA (56 Cards: 13 first, remaining 43 grouped by suit below) ---
    {
        id: 22,
        name: "Ace of Cups",
        arcana: "minor",
        suit: "cups",
        number: 1,
        uprightMeaning: "Wait for a new beginning in love, emotion, or creativity. Your heart is open, and overflow is possible. Offer forgiveness or follow a new passion.",
        keywords: ["Love", "New Feelings", "Intuition", "Creativity"],
        reflectionPrompt: "How can you open your heart to new emotional experiences?",
        imageUrl: "/assets/tarot/cups_ace.jpg"
    },
    {
        id: 23,
        name: "Two of Cups",
        arcana: "minor",
        suit: "cups",
        number: 2,
        uprightMeaning: "A partnership, mutual attraction, or strong connection. This card signifies harmony and balance between two people or two aspects of yourself.",
        keywords: ["Partnership", "Unity", "Love", "Connection"],
        reflectionPrompt: "Who or what are you harmonizing with today?",
        imageUrl: "/assets/tarot/cups_2.jpg"
    },
    {
        id: 24,
        name: "Three of Cups",
        arcana: "minor",
        suit: "cups",
        number: 3,
        uprightMeaning: "Celebration, friendship, and community. It is a time to gather with loved ones and share in the joy of connection and support.",
        keywords: ["Celebration", "Friendship", "Community", "Joy"],
        reflectionPrompt: "How can you celebrate your connections with others?",
        imageUrl: "/assets/tarot/cups_3.jpg"
    },
    {
        id: 25,
        name: "Ace of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 1,
        uprightMeaning: "A new financial or career opportunity. Manifestation of abundance and stability. Plant the seed for future prosperity.",
        keywords: ["Opportunity", "Prosperity", "New Venture", "Abundance"],
        reflectionPrompt: "What practical step can you take towards a new goal?",
        imageUrl: "/assets/tarot/pentacles_ace.jpg"
    },
    {
        id: 26,
        name: "Five of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 5,
        uprightMeaning: "Financial loss, poverty, or feeling left out in the cold. Seek help if you are struggling; do not let pride isolate you.",
        keywords: ["Hardship", "Loss", "Isolation", "Worry"],
        reflectionPrompt: "Where can you ask for help instead of struggling alone?",
        imageUrl: "/assets/tarot/pentacles_5.jpg"
    },
    {
        id: 27,
        name: "Ace of Swords",
        arcana: "minor",
        suit: "swords",
        number: 1,
        uprightMeaning: "A breakthrough, clarity, or sharp new idea. Cut through the confusion and see the truth. Mental power is at its peak.",
        keywords: ["Breakthrough", "Clarity", "Sharp Mind", "Truth"],
        reflectionPrompt: "What truth do you need to speak or acknowledge today?",
        imageUrl: "/assets/tarot/swords_ace.jpg"
    },
    {
        id: 28,
        name: "Three of Swords",
        arcana: "minor",
        suit: "swords",
        number: 3,
        uprightMeaning: "Heartbreak, sorrow, or grief. Allow yourself to feel the pain so it can be released. Healing comes after acknowledgment.",
        keywords: ["Heartbreak", "Sorrow", "Grief", "Pain"],
        reflectionPrompt: "What pain are you carrying that needs to be released?",
        imageUrl: "/assets/tarot/swords_3.jpg"
    },
    {
        id: 29,
        name: "Ace of Wands",
        arcana: "minor",
        suit: "wands",
        number: 1,
        uprightMeaning: "Inspiration, new opportunities, and creative potential. A spark of energy ignites a new passion or project. Take action now.",
        keywords: ["Inspiration", "New Opportunity", "Growth", "Potential"],
        reflectionPrompt: "What new passion is sparking within you right now?",
        imageUrl: "/assets/tarot/wands_ace.jpg"
    },
    {
        id: 30,
        name: "Four of Wands",
        arcana: "minor",
        suit: "wands",
        number: 4,
        uprightMeaning: "Celebration, joy, and harmony. A happy home environment or a milestone reached. Enjoy the stability and happiness.",
        keywords: ["Celebration", "Harmony", "Home", "Community"],
        reflectionPrompt: "What milestone or stability can you celebrate today?",
        imageUrl: "/assets/tarot/wands_4.jpg"
    },
    {
        id: 31,
        name: "Ten of Cups",
        arcana: "minor",
        suit: "cups",
        number: 10,
        uprightMeaning: "Emotional fulfillment, happiness, and alignment. A sense of wholeness in relationships and family life. A happily ever after moment.",
        keywords: ["Fulfillment", "Happiness", "Family", "Harmony"],
        reflectionPrompt: "Where do you feel most emotionally fulfilled and at home?",
        imageUrl: "/assets/tarot/cups_10.jpg"
    },
    {
        id: 32,
        name: "Ten of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 10,
        uprightMeaning: "Wealth, inheritance, and long-term success. Financial stability that lasts generations. Enjoy the fruits of your labor.",
        keywords: ["Wealth", "Legacy", "Family", "Long-term Success"],
        reflectionPrompt: "What legacy are you building for the future?",
        imageUrl: "/assets/tarot/pentacles_10.jpg"
    },
    {
        id: 33,
        name: "Queen of Wands",
        arcana: "minor",
        suit: "wands",
        number: 13,
        uprightMeaning: "Courage, confidence, and independence. A social butterfly who gets things done. Radiate your warmth and lead with passion.",
        keywords: ["Confidence", "Independence", "Social", "Passion"],
        reflectionPrompt: "How can you lead with more confidence and warmth today?",
        imageUrl: "/assets/tarot/wands_queen.jpg"
    },
    {
        id: 34,
        name: "King of Swords",
        arcana: "minor",
        suit: "swords",
        number: 14,
        uprightMeaning: "Intellectual power, authority, and truth. Using logic and reason to make fair decisions. Keep your emotions in check and think clearly.",
        keywords: ["Intellect", "Authority", "Truth", "Logic"],
        reflectionPrompt: "Where do you need to apply clear logic and reason?",
        imageUrl: "/assets/tarot/swords_king.jpg"
    },
    // --- CUPS ---
    {
        id: 35,
        name: "Four of Cups",
        arcana: "minor",
        suit: "cups",
        number: 4,
        uprightMeaning: "Apathy, contemplation, and re-evaluation. You may be so absorbed in what you already have, or what you lack, that you miss a new offer. Pause, then look up.",
        keywords: ["Apathy", "Contemplation", "Re-evaluation", "Disconnection"],
        reflectionPrompt: "What opportunity might you be overlooking while you're turned inward?",
        imageUrl: "/assets/tarot/cups_4.jpg"
    },
    {
        id: 36,
        name: "Five of Cups",
        arcana: "minor",
        suit: "cups",
        number: 5,
        uprightMeaning: "Loss, grief, and regret. Attention rests on what has spilled, yet two cups still stand behind you. Honor the sorrow, then turn toward what remains.",
        keywords: ["Grief", "Regret", "Loss", "Perspective"],
        reflectionPrompt: "What remains standing for you, even after this loss?",
        imageUrl: "/assets/tarot/cups_5.jpg"
    },
    {
        id: 37,
        name: "Six of Cups",
        arcana: "minor",
        suit: "cups",
        number: 6,
        uprightMeaning: "Nostalgia, childhood memories, and innocent kindness. A reunion with the past or an act of generosity from the heart. Let old joy inform today without trapping you in it.",
        keywords: ["Nostalgia", "Memories", "Innocence", "Kindness"],
        reflectionPrompt: "Which memory from your past still offers you comfort or wisdom?",
        imageUrl: "/assets/tarot/cups_6.jpg"
    },
    {
        id: 38,
        name: "Seven of Cups",
        arcana: "minor",
        suit: "cups",
        number: 7,
        uprightMeaning: "Choices, fantasy, and illusion. Many tempting possibilities appear at once, but not all are real. Discern which dreams deserve your commitment.",
        keywords: ["Choices", "Imagination", "Illusion", "Wishful Thinking"],
        reflectionPrompt: "Which of your options is grounded in reality, and which is only a daydream?",
        imageUrl: "/assets/tarot/cups_7.jpg"
    },
    {
        id: 39,
        name: "Eight of Cups",
        arcana: "minor",
        suit: "cups",
        number: 8,
        uprightMeaning: "Walking away in search of deeper meaning. What once fulfilled you no longer does, and you choose to leave for something more true. A courageous emotional departure.",
        keywords: ["Moving On", "Disillusionment", "Seeking Meaning", "Release"],
        reflectionPrompt: "What are you ready to leave behind in search of something more fulfilling?",
        imageUrl: "/assets/tarot/cups_8.jpg"
    },
    {
        id: 40,
        name: "Nine of Cups",
        arcana: "minor",
        suit: "cups",
        number: 9,
        uprightMeaning: "Contentment, satisfaction, and wishes granted. Often called the wish card: enjoy what you have earned and allow yourself to savor it.",
        keywords: ["Contentment", "Wishes", "Satisfaction", "Gratitude"],
        reflectionPrompt: "What are you grateful for in this moment that you could fully savor?",
        imageUrl: "/assets/tarot/cups_9.jpg"
    },
    {
        id: 41,
        name: "Page of Cups",
        arcana: "minor",
        suit: "cups",
        number: 11,
        uprightMeaning: "A tender message, creative spark, or intuitive nudge. Approach your feelings with curiosity and an open, childlike heart.",
        keywords: ["Creativity", "Intuition", "Curiosity", "Sensitivity"],
        reflectionPrompt: "What gentle inner message is asking for your attention today?",
        imageUrl: "/assets/tarot/cups_page.jpg"
    },
    {
        id: 42,
        name: "Knight of Cups",
        arcana: "minor",
        suit: "cups",
        number: 12,
        uprightMeaning: "Romance, charm, and following the heart. An invitation or offer arrives, led by emotion and idealism. Act on it sincerely, and keep your feet on the ground.",
        keywords: ["Romance", "Charm", "Idealism", "Invitation"],
        reflectionPrompt: "Where is your heart inviting you to move, and are you following it honestly?",
        imageUrl: "/assets/tarot/cups_knight.jpg"
    },
    {
        id: 43,
        name: "Queen of Cups",
        arcana: "minor",
        suit: "cups",
        number: 13,
        uprightMeaning: "Compassion, emotional maturity, and intuition. She feels deeply without being swept away. Trust your inner knowing and offer care without losing yourself.",
        keywords: ["Compassion", "Intuition", "Emotional Depth", "Nurturing"],
        reflectionPrompt: "How can you care for others while staying connected to your own feelings?",
        imageUrl: "/assets/tarot/cups_queen.jpg"
    },
    {
        id: 44,
        name: "King of Cups",
        arcana: "minor",
        suit: "cups",
        number: 14,
        uprightMeaning: "Emotional balance, diplomacy, and calm wisdom. Feelings are acknowledged and steered with maturity. Lead with empathy and composure.",
        keywords: ["Balance", "Diplomacy", "Calm", "Emotional Mastery"],
        reflectionPrompt: "Where can you respond with calm and empathy instead of reacting?",
        imageUrl: "/assets/tarot/cups_king.jpg"
    },
    // --- PENTACLES ---
    {
        id: 45,
        name: "Two of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 2,
        uprightMeaning: "Balancing priorities, adaptability, and juggling demands. Keep a steady rhythm and stay flexible; things are manageable when you stay present.",
        keywords: ["Balance", "Adaptability", "Priorities", "Flexibility"],
        reflectionPrompt: "Which of your commitments needs more attention, and which can wait?",
        imageUrl: "/assets/tarot/pentacles_2.jpg"
    },
    {
        id: 46,
        name: "Three of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 3,
        uprightMeaning: "Teamwork, craftsmanship, and collaboration. Skill is recognized when you work with others and build something with care.",
        keywords: ["Teamwork", "Skill", "Collaboration", "Craftsmanship"],
        reflectionPrompt: "Who could you collaborate with to bring out your best work?",
        imageUrl: "/assets/tarot/pentacles_3.jpg"
    },
    {
        id: 47,
        name: "Four of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 4,
        uprightMeaning: "Security, control, and holding on tightly. Stability is valuable, but clutching too hard can create rigidity and isolation.",
        keywords: ["Security", "Control", "Saving", "Possessiveness"],
        reflectionPrompt: "Where might your need for security be keeping you from growth or connection?",
        imageUrl: "/assets/tarot/pentacles_4.jpg"
    },
    {
        id: 48,
        name: "Six of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 6,
        uprightMeaning: "Generosity, fairness, and the flow of giving and receiving. Resources are shared; check that the exchange is balanced and freely given.",
        keywords: ["Generosity", "Fairness", "Sharing", "Balance"],
        reflectionPrompt: "How can you give and receive more freely and fairly?",
        imageUrl: "/assets/tarot/pentacles_6.jpg"
    },
    {
        id: 49,
        name: "Seven of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 7,
        uprightMeaning: "Patience, assessment, and long-term investment. You have planted seeds; pause to evaluate your progress before deciding where to place your energy next.",
        keywords: ["Patience", "Investment", "Evaluation", "Perseverance"],
        reflectionPrompt: "What have you been growing, and is it getting the care it needs?",
        imageUrl: "/assets/tarot/pentacles_7.jpg"
    },
    {
        id: 50,
        name: "Eight of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 8,
        uprightMeaning: "Dedication, mastery, and diligent practice. Steady, focused effort refines your craft. Commit to the process.",
        keywords: ["Dedication", "Mastery", "Diligence", "Skill Building"],
        reflectionPrompt: "What skill are you ready to practice patiently, one step at a time?",
        imageUrl: "/assets/tarot/pentacles_8.jpg"
    },
    {
        id: 51,
        name: "Nine of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 9,
        uprightMeaning: "Self-reliance, abundance, and refined independence. The fruit of your hard work is yours to enjoy in comfort.",
        keywords: ["Independence", "Abundance", "Self-Sufficiency", "Refinement"],
        reflectionPrompt: "How can you enjoy the rewards of your own effort today?",
        imageUrl: "/assets/tarot/pentacles_9.jpg"
    },
    {
        id: 52,
        name: "Page of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 11,
        uprightMeaning: "A new opportunity to learn, study, or begin something practical. Approach it with curiosity and take small, grounded steps.",
        keywords: ["Opportunity", "Study", "Ambition", "Groundedness"],
        reflectionPrompt: "What practical new skill or goal are you ready to begin learning?",
        imageUrl: "/assets/tarot/pentacles_page.jpg"
    },
    {
        id: 53,
        name: "Knight of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 12,
        uprightMeaning: "Reliability, hard work, and methodical progress. Slow and steady, he finishes what he starts. Stay consistent and thorough.",
        keywords: ["Reliability", "Diligence", "Routine", "Patience"],
        reflectionPrompt: "Where could steady, consistent effort move you toward your goal?",
        imageUrl: "/assets/tarot/pentacles_knight.jpg"
    },
    {
        id: 54,
        name: "Queen of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 13,
        uprightMeaning: "Practical nurturing, abundance, and comfort. She creates a warm, secure home and balances work with care. Ground your generosity in real action.",
        keywords: ["Nurturing", "Practicality", "Abundance", "Comfort"],
        reflectionPrompt: "How can you nourish yourself and those around you in practical ways?",
        imageUrl: "/assets/tarot/pentacles_queen.jpg"
    },
    {
        id: 55,
        name: "King of Pentacles",
        arcana: "minor",
        suit: "pentacles",
        number: 14,
        uprightMeaning: "Prosperity, stability, and sound leadership in material matters. Success built through patience and wise stewardship. Use your resources generously.",
        keywords: ["Prosperity", "Security", "Leadership", "Stewardship"],
        reflectionPrompt: "How can you steward your resources to build lasting security?",
        imageUrl: "/assets/tarot/pentacles_king.jpg"
    },
    // --- SWORDS ---
    {
        id: 56,
        name: "Two of Swords",
        arcana: "minor",
        suit: "swords",
        number: 2,
        uprightMeaning: "Indecision, stalemate, and avoidance. A blindfold keeps emotion and truth out of view. Remove it and face the choice.",
        keywords: ["Indecision", "Stalemate", "Avoidance", "Difficult Choice"],
        reflectionPrompt: "What truth are you avoiding that you need to see to make your decision?",
        imageUrl: "/assets/tarot/swords_2.jpg"
    },
    {
        id: 57,
        name: "Four of Swords",
        arcana: "minor",
        suit: "swords",
        number: 4,
        uprightMeaning: "Rest, recovery, and mental recuperation. Step back from the struggle to restore yourself. Rest is part of progress.",
        keywords: ["Rest", "Recovery", "Contemplation", "Restoration"],
        reflectionPrompt: "What would genuine rest look like for you right now?",
        imageUrl: "/assets/tarot/swords_4.jpg"
    },
    {
        id: 58,
        name: "Five of Swords",
        arcana: "minor",
        suit: "swords",
        number: 5,
        uprightMeaning: "Conflict, win-at-all-costs thinking, and hollow victory. Consider whether the fight is worth its cost, and choose your battles.",
        keywords: ["Conflict", "Defeat", "Tension", "Hollow Victory"],
        reflectionPrompt: "Is the battle you are fighting worth what it may cost you?",
        imageUrl: "/assets/tarot/swords_5.jpg"
    },
    {
        id: 59,
        name: "Six of Swords",
        arcana: "minor",
        suit: "swords",
        number: 6,
        uprightMeaning: "Transition, moving on, and calmer waters ahead. You are leaving difficulty behind while carrying what you must. Healing comes through the journey.",
        keywords: ["Transition", "Moving On", "Healing", "Passage"],
        reflectionPrompt: "What are you carrying forward, and what can you set down as you move on?",
        imageUrl: "/assets/tarot/swords_6.jpg"
    },
    {
        id: 60,
        name: "Seven of Swords",
        arcana: "minor",
        suit: "swords",
        number: 7,
        uprightMeaning: "Strategy, secrecy, and acting alone. You may be sidestepping a direct approach, or someone may not be fully honest. Be thoughtful about integrity.",
        keywords: ["Strategy", "Stealth", "Deception", "Resourcefulness"],
        reflectionPrompt: "Where would honesty serve you better than a clever workaround?",
        imageUrl: "/assets/tarot/swords_7.jpg"
    },
    {
        id: 61,
        name: "Eight of Swords",
        arcana: "minor",
        suit: "swords",
        number: 8,
        uprightMeaning: "Feeling trapped, restricted, and powerless. The bindings are looser than they seem; the way out begins with a shift in perspective.",
        keywords: ["Restriction", "Self-Doubt", "Feeling Stuck", "Perspective"],
        reflectionPrompt: "Which limiting belief might loosen if you looked at it differently?",
        imageUrl: "/assets/tarot/swords_8.jpg"
    },
    {
        id: 62,
        name: "Nine of Swords",
        arcana: "minor",
        suit: "swords",
        number: 9,
        uprightMeaning: "Anxiety, worry, and sleepless nights. Fears often loom larger in the mind than in reality. Reach out for support and name what troubles you.",
        keywords: ["Anxiety", "Worry", "Fear", "Overthinking"],
        reflectionPrompt: "What worry would feel lighter if you shared it with someone you trust?",
        imageUrl: "/assets/tarot/swords_9.jpg"
    },
    {
        id: 63,
        name: "Ten of Swords",
        arcana: "minor",
        suit: "swords",
        number: 10,
        uprightMeaning: "Painful endings, rock bottom, and release. The worst is behind you; from here, a new dawn awaits.",
        keywords: ["Endings", "Rock Bottom", "Release", "New Dawn"],
        reflectionPrompt: "What has fully ended, and what new day is beginning because of it?",
        imageUrl: "/assets/tarot/swords_10.jpg"
    },
    {
        id: 64,
        name: "Page of Swords",
        arcana: "minor",
        suit: "swords",
        number: 11,
        uprightMeaning: "Curiosity, new ideas, and a hunger for truth. Stay alert, ask questions, and think before you speak.",
        keywords: ["Curiosity", "Ideas", "Vigilance", "Communication"],
        reflectionPrompt: "What question are you curious enough to start exploring today?",
        imageUrl: "/assets/tarot/swords_page.jpg"
    },
    {
        id: 65,
        name: "Knight of Swords",
        arcana: "minor",
        suit: "swords",
        number: 12,
        uprightMeaning: "Ambition, speed, and direct action. He charges toward goals with sharp focus; consider whether haste may overlook other people or details.",
        keywords: ["Ambition", "Speed", "Directness", "Impulsiveness"],
        reflectionPrompt: "Where would a moment's pause improve your next move?",
        imageUrl: "/assets/tarot/swords_knight.jpg"
    },
    {
        id: 66,
        name: "Queen of Swords",
        arcana: "minor",
        suit: "swords",
        number: 13,
        uprightMeaning: "Clear perception, independence, and honest communication. She sees through illusion and speaks plainly and fairly.",
        keywords: ["Clarity", "Independence", "Honesty", "Discernment"],
        reflectionPrompt: "Where can you speak a clear, honest truth with kindness?",
        imageUrl: "/assets/tarot/swords_queen.jpg"
    },
    // --- WANDS ---
    {
        id: 67,
        name: "Two of Wands",
        arcana: "minor",
        suit: "wands",
        number: 2,
        uprightMeaning: "Planning, decisions, and future vision. You have achieved something and now look at what lies beyond. Choose a direction and step toward it.",
        keywords: ["Planning", "Vision", "Decisions", "Discovery"],
        reflectionPrompt: "Where do you want to go next, and what first step could you take?",
        imageUrl: "/assets/tarot/wands_2.jpg"
    },
    {
        id: 68,
        name: "Three of Wands",
        arcana: "minor",
        suit: "wands",
        number: 3,
        uprightMeaning: "Expansion, foresight, and progress. Your efforts are underway and opportunities are arriving from afar. Keep looking ahead.",
        keywords: ["Expansion", "Foresight", "Progress", "Opportunity"],
        reflectionPrompt: "What are you ready to expand toward as your plans take shape?",
        imageUrl: "/assets/tarot/wands_3.jpg"
    },
    {
        id: 69,
        name: "Five of Wands",
        arcana: "minor",
        suit: "wands",
        number: 5,
        uprightMeaning: "Competition, friction, and diverse viewpoints. A clash of ideas can be chaotic, but also energizing. Find constructive ways to channel it.",
        keywords: ["Competition", "Conflict", "Diversity", "Tension"],
        reflectionPrompt: "How can you turn creative friction into growth instead of conflict?",
        imageUrl: "/assets/tarot/wands_5.jpg"
    },
    {
        id: 70,
        name: "Six of Wands",
        arcana: "minor",
        suit: "wands",
        number: 6,
        uprightMeaning: "Victory, recognition, and public acknowledgment. Hard work is noticed; accept the praise while staying grounded.",
        keywords: ["Victory", "Recognition", "Success", "Confidence"],
        reflectionPrompt: "What recent success deserves to be acknowledged and celebrated?",
        imageUrl: "/assets/tarot/wands_6.jpg"
    },
    {
        id: 71,
        name: "Seven of Wands",
        arcana: "minor",
        suit: "wands",
        number: 7,
        uprightMeaning: "Standing your ground, defense, and perseverance. You hold your position against challenges. Trust your convictions and keep your footing.",
        keywords: ["Perseverance", "Defense", "Courage", "Conviction"],
        reflectionPrompt: "What are you willing to stand up for, even when it is hard?",
        imageUrl: "/assets/tarot/wands_7.jpg"
    },
    {
        id: 72,
        name: "Eight of Wands",
        arcana: "minor",
        suit: "wands",
        number: 8,
        uprightMeaning: "Swift movement, momentum, and rapid developments. Things accelerate and messages arrive quickly. Stay alert and ready to act.",
        keywords: ["Speed", "Momentum", "Action", "Communication"],
        reflectionPrompt: "What is moving quickly in your life right now, and how can you meet it with awareness?",
        imageUrl: "/assets/tarot/wands_8.jpg"
    },
    {
        id: 73,
        name: "Nine of Wands",
        arcana: "minor",
        suit: "wands",
        number: 9,
        uprightMeaning: "Resilience, persistence, and the last stretch. You are battle-weary but nearly there. Stay guarded, and trust your strength.",
        keywords: ["Resilience", "Persistence", "Boundaries", "Stamina"],
        reflectionPrompt: "What strength have you built that can carry you through the final stretch?",
        imageUrl: "/assets/tarot/wands_9.jpg"
    },
    {
        id: 74,
        name: "Ten of Wands",
        arcana: "minor",
        suit: "wands",
        number: 10,
        uprightMeaning: "Burden, responsibility, and overextension. You have taken on a great deal; consider what can be delegated, shared, or set down.",
        keywords: ["Burden", "Responsibility", "Overload", "Hard Work"],
        reflectionPrompt: "What are you carrying that you could share or put down?",
        imageUrl: "/assets/tarot/wands_10.jpg"
    },
    {
        id: 75,
        name: "Page of Wands",
        arcana: "minor",
        suit: "wands",
        number: 11,
        uprightMeaning: "Enthusiasm, exploration, and fresh creative energy. A spark of inspiration or news encourages you to try something new.",
        keywords: ["Enthusiasm", "Exploration", "Inspiration", "Free Spirit"],
        reflectionPrompt: "What new creative spark are you eager to explore?",
        imageUrl: "/assets/tarot/wands_page.jpg"
    },
    {
        id: 76,
        name: "Knight of Wands",
        arcana: "minor",
        suit: "wands",
        number: 12,
        uprightMeaning: "Energy, passion, and adventure. He charges ahead with fiery drive; channel his boldness, and mind impatience.",
        keywords: ["Energy", "Passion", "Adventure", "Impulsiveness"],
        reflectionPrompt: "Where can you direct your passion into purposeful action?",
        imageUrl: "/assets/tarot/wands_knight.jpg"
    },
    {
        id: 77,
        name: "King of Wands",
        arcana: "minor",
        suit: "wands",
        number: 14,
        uprightMeaning: "Vision, leadership, and bold inspiration. He turns ideas into reality and inspires others. Lead with integrity and big-picture clarity.",
        keywords: ["Leadership", "Vision", "Charisma", "Boldness"],
        reflectionPrompt: "What vision are you ready to lead others toward, with integrity?",
        imageUrl: "/assets/tarot/wands_king.jpg"
    }
];

// Helper to get a random card
export const getRandomCard = (excludeIds: number[] = []): TarotCard => {
    const availableCards = tarotDeck.filter(card => !excludeIds.includes(card.id));

    // If we've exhausted the deck, reset (or just pick from full deck)
    // Logic: if available is empty, ignored exclusion list to avoid crash
    const pool = availableCards.length > 0 ? availableCards : tarotDeck;

    const randomIndex = Math.floor(Math.random() * pool.length);
    return pool[randomIndex];
};
